/**
 * BookingPage — Universal booking hub.
 *
 * Tabs:
 *   • Calendar   (day/week view of upcoming appointments)
 *   • Setup      (pick industry template, seed resources+services+fields)
 *   • Resources  (chairs, rooms, practitioners, tables — CRUD)
 *   • Services   (services offered — CRUD)
 *   • Fields     (custom-field builder for the intake form)
 *   • Reports    (revenue, no-show rate, top clients, busiest hours)
 *   • Automations(reminder & follow-up rules)
 *
 * Every booking created here upserts a CRM contact — the "all clients
 * connect to CRM" standing order is enforced at the API layer, and the
 * "New Booking" flow surfaces the contact-lookup step first.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Calendar, Users, Wrench, ListChecks, BarChart3, Bell, Plus, Sparkles,
  Clock, Trash2, Pencil, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import IndustryPicker from '../components/booking/IndustryPicker';
import NewBookingModal from '../components/booking/NewBookingModal';
import BookingCalendarView from '../components/booking/BookingCalendarView';
import BookingReports from '../components/booking/BookingReports';
import CustomFieldsBuilder from '../components/booking/CustomFieldsBuilder';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BookingPage() {
  const [tab, setTab] = useState('calendar');
  const [profile, setProfile] = useState(null);
  const [resources, setResources] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [newBookingOpen, setNewBookingOpen] = useState(false);
  const [resourceEditOpen, setResourceEditOpen] = useState(null);
  const [serviceEditOpen, setServiceEditOpen] = useState(null);
  const [loading, setLoading] = useState(true);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const loadAll = async () => {
    try {
      const [p, r, s, b] = await Promise.all([
        axios.get(`${API}/api/settings/tenant`, auth()).catch(() => ({ data: null })),
        axios.get(`${API}/api/booking/resources`, auth()),
        axios.get(`${API}/api/booking/services`, auth()),
        axios.get(`${API}/api/booking/bookings?limit=200`, auth()),
      ]);
      setProfile(p.data);
      setResources(r.data || []);
      setServices(s.data || []);
      setBookings(b.data || []);
    } catch (e) {
      console.error('Booking load error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const hasSetup = resources.length > 0 && services.length > 0;

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="booking-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
            <Calendar className="h-7 w-7 text-emerald-brand" />
            Booking
          </h1>
          <p className="text-sm text-gray-medium mt-1">
            Universal booking engine — dental, medical, pet, beauty, home services, and 45+ more verticals.
            Every booking auto-syncs to CRM.
          </p>
        </div>
        {hasSetup && (
          <Button
            className="bg-emerald-brand hover:bg-emerald-hover gap-2"
            onClick={() => setNewBookingOpen(true)}
            data-testid="new-booking-btn"
          >
            <Plus className="h-4 w-4" /> New Booking
          </Button>
        )}
      </div>

      {/* Onboarding prompt */}
      {!loading && !hasSetup && (
        <Card className="mb-6 border-emerald-200 bg-emerald-50/50" data-testid="booking-onboarding-card">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Sparkles className="h-6 w-6 text-emerald-brand flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-lg text-text-primary">Choose your industry to get started</h3>
                <p className="text-sm text-gray-medium mt-1">
                  Pick a template and we&apos;ll pre-configure resources, services, custom fields, and reminders
                  tuned for your business — all editable later.
                </p>
                <Button className="mt-3 bg-emerald-brand" onClick={() => setTab('setup')} data-testid="onboarding-cta">
                  Pick Industry Template
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-3 sm:grid-cols-7 mb-6 h-auto" data-testid="booking-tabs">
          <TabsTrigger value="calendar" className="gap-1.5" data-testid="tab-calendar"><Calendar className="h-4 w-4" /><span className="hidden sm:inline">Calendar</span></TabsTrigger>
          <TabsTrigger value="setup" className="gap-1.5" data-testid="tab-setup"><Sparkles className="h-4 w-4" /><span className="hidden sm:inline">Setup</span></TabsTrigger>
          <TabsTrigger value="resources" className="gap-1.5" data-testid="tab-resources"><Wrench className="h-4 w-4" /><span className="hidden sm:inline">Resources</span></TabsTrigger>
          <TabsTrigger value="services" className="gap-1.5" data-testid="tab-services"><ListChecks className="h-4 w-4" /><span className="hidden sm:inline">Services</span></TabsTrigger>
          <TabsTrigger value="fields" className="gap-1.5" data-testid="tab-fields"><Pencil className="h-4 w-4" /><span className="hidden sm:inline">Fields</span></TabsTrigger>
          <TabsTrigger value="reports" className="gap-1.5" data-testid="tab-reports"><BarChart3 className="h-4 w-4" /><span className="hidden sm:inline">Reports</span></TabsTrigger>
          <TabsTrigger value="automations" className="gap-1.5" data-testid="tab-automations"><Bell className="h-4 w-4" /><span className="hidden sm:inline">Automations</span></TabsTrigger>
        </TabsList>

        <TabsContent value="calendar">
          <BookingCalendarView
            bookings={bookings}
            resources={resources}
            services={services}
            onReload={loadAll}
          />
        </TabsContent>

        <TabsContent value="setup">
          <IndustryPicker onSetupComplete={loadAll} />
        </TabsContent>

        <TabsContent value="resources">
          <ResourcesTab
            resources={resources}
            onEdit={setResourceEditOpen}
            onReload={loadAll}
            api={API}
            auth={auth}
          />
        </TabsContent>

        <TabsContent value="services">
          <ServicesTab
            services={services}
            resources={resources}
            onEdit={setServiceEditOpen}
            onReload={loadAll}
            api={API}
            auth={auth}
          />
        </TabsContent>

        <TabsContent value="fields">
          <CustomFieldsBuilder api={API} auth={auth} />
        </TabsContent>

        <TabsContent value="reports">
          <BookingReports api={API} auth={auth} />
        </TabsContent>

        <TabsContent value="automations">
          <AutomationsTab api={API} auth={auth} />
        </TabsContent>
      </Tabs>

      {newBookingOpen && (
        <NewBookingModal
          open={newBookingOpen}
          onClose={() => setNewBookingOpen(false)}
          onCreated={loadAll}
          resources={resources}
          services={services}
        />
      )}

      {resourceEditOpen && (
        <ResourceEditDialog
          resource={resourceEditOpen}
          onClose={() => setResourceEditOpen(null)}
          onSaved={() => { setResourceEditOpen(null); loadAll(); }}
          api={API}
          auth={auth}
        />
      )}

      {serviceEditOpen && (
        <ServiceEditDialog
          service={serviceEditOpen}
          resources={resources}
          onClose={() => setServiceEditOpen(null)}
          onSaved={() => { setServiceEditOpen(null); loadAll(); }}
          api={API}
          auth={auth}
        />
      )}
    </div>
  );
}


/* ─── Resources tab ────────────────────────────────────────────────────── */
function ResourcesTab({ resources, onEdit, onReload, api, auth }) {
  const create = () => onEdit({ label: '', type: 'chair', capacity: 1, __new: true });
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.label}"?`)) return;
    await axios.delete(`${api}/api/booking/resources/${r.id}`, auth());
    toast.success('Resource deleted');
    onReload();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-medium">Chairs, rooms, practitioners, tables — anything a booking can occupy.</p>
        <Button onClick={create} size="sm" className="gap-1.5" data-testid="add-resource-btn"><Plus className="h-4 w-4" />Add Resource</Button>
      </div>
      {resources.length === 0 ? (
        <p className="text-sm text-gray-medium text-center py-12">No resources yet — run Setup or add one manually.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {resources.map((r) => (
            <Card key={r.id} className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`resource-${r.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium text-text-primary">{r.label}</div>
                    <div className="text-xs text-gray-medium mt-1 capitalize">{r.type}</div>
                    <div className="text-xs text-gray-medium">Capacity: {r.capacity}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(r)} data-testid={`edit-resource-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(r)} data-testid={`delete-resource-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── Services tab ─────────────────────────────────────────────────────── */
function ServicesTab({ services, onEdit, onReload, api, auth }) {
  const create = () => onEdit({ name: '', duration_min: 30, price_min: 0, price_max: 0, __new: true });
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await axios.delete(`${api}/api/booking/services/${s.id}`, auth());
    toast.success('Service deleted');
    onReload();
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-medium">Services you offer, with duration & price range.</p>
        <Button onClick={create} size="sm" className="gap-1.5" data-testid="add-service-btn"><Plus className="h-4 w-4" />Add Service</Button>
      </div>
      {services.length === 0 ? (
        <p className="text-sm text-gray-medium text-center py-12">No services yet.</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {services.map((s) => (
            <Card key={s.id} className="hover:shadow-md transition-shadow" data-testid={`service-${s.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-text-primary">{s.name}</div>
                    <div className="text-xs text-gray-medium mt-1 flex items-center gap-3 flex-wrap">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{s.duration_min} min</span>
                      {(s.price_min > 0 || s.price_max > 0) && (
                        <span>${s.price_min}{s.price_max > s.price_min ? `–$${s.price_max}` : ''}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => onEdit(s)} data-testid={`edit-service-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" onClick={() => remove(s)} data-testid={`delete-service-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


/* ─── Automations tab ──────────────────────────────────────────────────── */
function AutomationsTab({ api, auth }) {
  const [automations, setAutomations] = useState([]);
  const [notifications, setNotifications] = useState([]);
  useEffect(() => {
    axios.get(`${api}/api/booking/notifications?limit=50`, auth()).then((r) => setNotifications(r.data || [])).catch(() => {});
  }, [api]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle className="text-lg">Reminder & Follow-up Rules</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-gray-medium mb-4">
            Email reminders fire automatically at 24 h and 2 h before every appointment,
            plus a review request 24 h after completion. SMS, WhatsApp, and voice channels
            will unlock in a future release — the rule engine already routes them internally.
          </p>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium">Booking Confirmation</div>
                <div className="text-xs text-gray-medium">Fires immediately after a booking is created</div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Email · Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium">24-hour Reminder</div>
                <div className="text-xs text-gray-medium">Fires 24 h before the appointment</div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Email · Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium">2-hour Reminder</div>
                <div className="text-xs text-gray-medium">Final nudge to reduce no-shows</div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Email · Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium">Post-Visit Review Request</div>
                <div className="text-xs text-gray-medium">Sends 24 h after completion — feeds Reputation module</div>
              </div>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Email · Active</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-60">
              <div>
                <div className="font-medium">SMS Channel</div>
                <div className="text-xs text-gray-medium">Coming — Twilio integration</div>
              </div>
              <Badge variant="outline">Provisioned</Badge>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg opacity-60">
              <div>
                <div className="font-medium">WhatsApp Channel</div>
                <div className="text-xs text-gray-medium">Coming — Twilio WhatsApp Business API</div>
              </div>
              <Badge variant="outline">Provisioned</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg">Recent Notifications</CardTitle></CardHeader>
        <CardContent>
          {notifications.length === 0 ? (
            <p className="text-sm text-gray-medium">No notifications sent yet.</p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {notifications.map((n) => (
                <div key={n.id} className="flex items-center justify-between p-3 bg-gray-50 rounded text-sm" data-testid={`notif-${n.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{n.subject}</div>
                    <div className="text-xs text-gray-medium">To: {n.to} · {n.kind}</div>
                  </div>
                  <Badge variant="outline" className="ml-2 flex-shrink-0">{n.channel}</Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ─── Resource edit dialog ─────────────────────────────────────────────── */
function ResourceEditDialog({ resource, onClose, onSaved, api, auth }) {
  const [form, setForm] = useState({
    label: resource.label || '', type: resource.type || 'chair',
    capacity: resource.capacity || 1,
  });
  const save = async () => {
    try {
      if (resource.__new) {
        await axios.post(`${api}/api/booking/resources`, form, auth());
        toast.success('Resource created');
      } else {
        await axios.patch(`${api}/api/booking/resources/${resource.id}`, form, auth());
        toast.success('Resource updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="resource-edit-dialog">
        <DialogHeader><DialogTitle>{resource.__new ? 'Add Resource' : 'Edit Resource'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Label</Label><Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="resource-label" /></div>
          <div><Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="resource-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['chair','room','practitioner','table','bay','station','court','lane','desk','kennel','vehicle','venue','equipment','generic'].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Capacity</Label><Input type="number" min="1" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value) || 1 })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-emerald-brand" data-testid="save-resource-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ─── Service edit dialog ──────────────────────────────────────────────── */
function ServiceEditDialog({ service, resources, onClose, onSaved, api, auth }) {
  const resourceTypes = [...new Set(resources.map((r) => r.type))];
  const [form, setForm] = useState({
    name: service.name || '', duration_min: service.duration_min || 30,
    price_min: service.price_min || 0, price_max: service.price_max || 0,
    buffer_before_min: service.buffer_before_min || 0, buffer_after_min: service.buffer_after_min || 5,
    required_resource_types: service.required_resource_types || [],
  });
  const save = async () => {
    try {
      if (service.__new) {
        await axios.post(`${api}/api/booking/services`, form, auth());
        toast.success('Service created');
      } else {
        await axios.patch(`${api}/api/booking/services/${service.id}`, form, auth());
        toast.success('Service updated');
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };
  const toggleType = (t) => {
    const has = form.required_resource_types.includes(t);
    setForm({ ...form, required_resource_types: has ? form.required_resource_types.filter((x) => x !== t) : [...form.required_resource_types, t] });
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="service-edit-dialog">
        <DialogHeader><DialogTitle>{service.__new ? 'Add Service' : 'Edit Service'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="service-name" /></div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Duration (min)</Label><Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} data-testid="service-duration" /></div>
            <div><Label>Price min</Label><Input type="number" value={form.price_min} onChange={(e) => setForm({ ...form, price_min: parseFloat(e.target.value) || 0 })} /></div>
            <div><Label>Price max</Label><Input type="number" value={form.price_max} onChange={(e) => setForm({ ...form, price_max: parseFloat(e.target.value) || 0 })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Buffer before (min)</Label><Input type="number" value={form.buffer_before_min} onChange={(e) => setForm({ ...form, buffer_before_min: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Buffer after (min)</Label><Input type="number" value={form.buffer_after_min} onChange={(e) => setForm({ ...form, buffer_after_min: parseInt(e.target.value) || 0 })} /></div>
          </div>
          {resourceTypes.length > 0 && (
            <div>
              <Label className="mb-2 block">Required Resource Types</Label>
              <div className="flex flex-wrap gap-2">
                {resourceTypes.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleType(t)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.required_resource_types.includes(t) ? 'bg-emerald-brand text-white border-emerald-brand' : 'bg-white text-text-primary border-gray-200 hover:border-gray-400'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-emerald-brand" data-testid="save-service-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
